import { Component, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Utilities } from '@core/utils/utilities';
import { AccessControlService } from './core/services/access-control.service';
import { AuthService } from './core/services/auth.service';
import { environment } from '@environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, from } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  standalone: false
})

export class AppComponent {
  util = inject(Utilities);
  auth = inject(AuthService);
  router = inject(Router);
  accessControlService = inject(AccessControlService);
  dialog = inject(MatDialog);
  displayPage = signal(true);
  pageAccess = signal<{ subjectClassification: boolean, userManagement: boolean, forwardGeneration: boolean, reportExtraction: boolean }>({ subjectClassification: false, userManagement: false, forwardGeneration: false, reportExtraction: false });
  localStorage: any = localStorage;
  pageDetailsFetched = signal(false);
  showInstitutionChangePopup = signal(false);
  changedInstitutionName = signal('');
  changedInstPageUrl = '';

  constructor() {
    this.util.setCKEditorDefaultConfig();
    this.checkSubjectClassificationPageAccess();
    // Watch for institution changes from other tabs
    effect(() => {
      const changeDetails = this.util.institutionChangedInOtherTab();
      if (changeDetails) {
        this.changedInstitutionName.set(changeDetails.institutionName || '');
        this.changedInstPageUrl = changeDetails.instPageUrl || '';
        this.showInstitutionChangePopup.set(true);
      }
    });
  }

  handleInstitutionChangeRedirect() {
    this.showInstitutionChangePopup.set(false);
    this.util.institutionChangedInOtherTab.set(null);
    // Close all open Material dialogs
    this.dialog.closeAll();
    this.util.isDialogOpen$.set(false);
    // Sync currentInstitutionId so this tab won't trigger a false popup
    this.util.getInstituionDetails();
    if (this.changedInstPageUrl) {
      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      this.router.navigateByUrl(this.changedInstPageUrl);
    }
  }

  onNavigate(event: any) {
    if (event.detail) {
      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      this.router.navigateByUrl(event.detail);
    }
  }

  checkSubjectClassificationPageAccess() {
    if (this.util.getStoredUserDetails())
      forkJoin({
        subjectClassification: this.accessControlService.getPageAccess('subject-classification'),
        forwardGeneration: this.accessControlService.getPageAccess('forward-generation'),
        reportExtraction: this.accessControlService.getPageAccess('report-extraction'),
        userManagement: from(this.util.fetchFeatureFlagDetails(environment.gitAccessDetails, environment.gitProjId, ('scc_add_new_user_access' + (environment.name ? '_' + environment.name : '')))),
        forwardGenerationFeatureFlag: from(this.util.fetchFeatureFlagDetails(environment.gitAccessDetails, environment.gitProjId, ('scc_forward_generation_access' + (environment.name ? '_' + environment.name : ''))))
      }).subscribe({
        next: (res: any) => {
          this.accessControlService.pageAccess$.update(pages => ({
            ...pages,
            'subject-classification': res.subjectClassification,
            'forward-generation': res.forwardGeneration,
            'report-extraction': res.reportExtraction
          }));

          const scPageDetails = res.subjectClassification[this.util.getStoredUserDetails()?.roleName];
          const scAccess = scPageDetails ? Object.values(scPageDetails)[0] as boolean : false;

          const fgPageDetails = res.forwardGeneration[this.util.getStoredUserDetails()?.roleName];
          const roleAccess = fgPageDetails ? Object.values(fgPageDetails)[0] as boolean : false;
          const fgAccess = roleAccess && res.forwardGenerationFeatureFlag;

          const rePageDetails = res.reportExtraction[this.util.getStoredUserDetails()?.roleName];
          const reAccess = rePageDetails ? Object.values(rePageDetails)[0] as boolean : false;

          this.pageAccess.set({
            subjectClassification: scAccess,
            userManagement: res.userManagement,
            forwardGeneration: fgAccess,
            reportExtraction: reAccess
          });

          this.pageDetailsFetched.set(true);
        }
      });
    else
      this.pageDetailsFetched.set(true);
  }
}
